'use client'

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { X, MessageSquare, Bug, Lightbulb, Send, Check, Camera, Trash2 } from 'lucide-react'
import { Button } from './Button'
import { Input } from './Input'
import { Textarea } from './Textarea'
import { useFeedbackStore, type FeedbackType } from '../stores/feedbackStore'
import { useEnabledModules } from '../hooks/useEnabledModules'
import { appIdFromPath } from '../lib/errorReport/errorReport'
import { capturePageScreenshot } from '../lib/errorReport/captureScreenshot'
import { uploadScreenshot } from '../lib/errorReport/uploadScreenshot'

const feedbackTypes: { type: FeedbackType; label: string; icon: typeof Bug; description: string }[] = [
  { type: 'bug', label: 'Bug Report', icon: Bug, description: 'Something isn\'t working' },
  { type: 'feature', label: 'Feature Request', icon: Lightbulb, description: 'Suggest an improvement' },
  { type: 'general', label: 'General Feedback', icon: MessageSquare, description: 'Share your thoughts' },
]

const GENERAL_APP = '__general__'

export function FeedbackModal() {
  const { isFeedbackOpen, feedbackType, closeFeedback } = useFeedbackStore()
  const { modules } = useEnabledModules()
  const pathname = usePathname()

  const [selectedType, setSelectedType] = useState<FeedbackType>(feedbackType)
  const [appId, setAppId] = useState<string>(GENERAL_APP)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [screenshot, setScreenshot] = useState<Blob | null>(null)
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null)
  const [capturing, setCapturing] = useState(false)

  // Only offer apps the user actually has enabled. The current app (if we're on
  // one and it's enabled) is preselected as a convenience.
  const currentAppId = useMemo(() => appIdFromPath(pathname), [pathname])
  // Stable key for the enabled app set so the open-time effect doesn't re-run
  // on every render (useEnabledModules returns a fresh array each call, which
  // would otherwise keep resetting the user's app/type selection).
  const enabledIds = modules.map((m) => m.id).join(',')

  useEffect(() => {
    if (!isFeedbackOpen) return
    // Sync the type chosen at open time, and default the app dropdown to the
    // app the user is currently in (when it's one they have enabled).
    setSelectedType(feedbackType)
    const isEnabled = enabledIds.split(',').includes(currentAppId ?? '')
    setAppId(isEnabled && currentAppId ? currentAppId : GENERAL_APP)
  }, [isFeedbackOpen, feedbackType, currentAppId, enabledIds])

  // Object URL for the screenshot thumbnail, revoked when the shot changes.
  useEffect(() => {
    if (!screenshot) {
      setScreenshotUrl(null)
      return
    }
    const url = URL.createObjectURL(screenshot)
    setScreenshotUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [screenshot])

  if (!isFeedbackOpen) return null

  const handleCaptureScreenshot = async () => {
    if (capturing) return
    setCapturing(true)
    try {
      // The modal is marked data-screenshot-exclude, so the capture shows the
      // page behind it — the thing the user is actually reporting about.
      const blob = await capturePageScreenshot()
      if (blob) {
        setScreenshot(blob)
      } else {
        setError('Could not capture a screenshot on this device')
      }
    } finally {
      setCapturing(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!description.trim()) {
      setError('Please provide some feedback')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const screenshotPath = screenshot ? await uploadScreenshot(screenshot) : null

      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: selectedType,
          appId: appId === GENERAL_APP ? null : appId,
          subject: title.trim() || undefined,
          description: description.trim(),
          url: typeof window !== 'undefined' ? window.location.href : undefined,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
          screenshotPath,
        }),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => null)
        throw new Error(data?.error || 'Failed to submit feedback')
      }

      setSubmitted(true)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to submit feedback. Please try again.'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setTitle('')
    setDescription('')
    setError(null)
    setSubmitted(false)
    setSelectedType('general')
    setAppId(GENERAL_APP)
    setScreenshot(null)
    closeFeedback()
  }

  if (submitted) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Thank you for your feedback!
            </h3>
            <p className="text-sm text-gray-600">
              We appreciate you taking the time to help us improve.
            </p>
            <div className="flex items-center justify-center gap-3 mt-5">
              <Button variant="secondary" onClick={handleClose}>
                Done
              </Button>
              <Link
                href="/support"
                onClick={handleClose}
                className="text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                View my requests
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-screenshot-exclude>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Send Feedback</h2>
          <button
            onClick={handleClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto">
          {/* Feedback Type Selector */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              What type of feedback?
            </label>
            <div className="grid grid-cols-3 gap-2">
              {feedbackTypes.map(({ type, label, icon: Icon }) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setSelectedType(type)}
                  className={`
                    flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-colors
                    ${selectedType === type
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-xs font-medium">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* App Selector — restricted to the user's enabled apps */}
          <div className="space-y-1 w-full">
            <label htmlFor="feedback-app" className="block text-sm font-medium text-gray-700">
              Which app is this about?
            </label>
            <select
              id="feedback-app"
              value={appId}
              onChange={(e) => setAppId(e.target.value)}
              className="
                w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm bg-white
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
              "
            >
              <option value={GENERAL_APP}>General / Whole app</option>
              {modules.map((app) => (
                <option key={app.id} value={app.id}>
                  {app.name}
                </option>
              ))}
            </select>
          </div>

          {/* Title (optional) */}
          <Input
            label="Subject (optional)"
            placeholder="Brief summary of your feedback"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          {/* Screenshot — captured from the page behind the modal */}
          <div className="space-y-2">
            {screenshotUrl ? (
              <>
                <div className="flex items-start gap-3">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={screenshotUrl}
                    alt="Screenshot that will be attached"
                    className="max-h-32 rounded-lg border border-gray-200 object-contain"
                  />
                  <button
                    type="button"
                    onClick={() => setScreenshot(null)}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Remove
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  This shows everything that was on the page, so check it for anything
                  personal before sending. Only you and support can see it.
                </p>
              </>
            ) : (
              <button
                type="button"
                onClick={handleCaptureScreenshot}
                disabled={capturing}
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 disabled:opacity-60 transition-colors"
              >
                <Camera className="w-4 h-4" />
                {capturing ? 'Capturing…' : 'Attach a screenshot of this page'}
              </button>
            )}
          </div>

          {/* Description */}
          <Textarea
            label="Your feedback"
            placeholder={
              selectedType === 'bug'
                ? 'Please describe what happened and what you expected to happen...'
                : selectedType === 'feature'
                ? 'Please describe the feature you\'d like to see...'
                : 'Share your thoughts with us...'
            }
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={error || undefined}
          />

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !description.trim()}>
              {loading ? (
                'Submitting...'
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Feedback
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
